import React, { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { Text, Button, Flex } from "@radix-ui/themes";
import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";
import { SessionsSearchForm } from "@/pages/sessions/root/sessions-search-form";
import { SessionsTable } from "@/pages/sessions/root/sessions-table";
import { SessionSummarySchema } from "@/domains/openapi/types";

export const SessionsRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL query parameters
  const pageSize = 50;
  const initialQuery = searchParams.get("query") ?? "";
  const [searchTerm, setSearchTerm] = useState<string>(initialQuery);
  const [inputValue, setInputValue] = useState<string>(initialQuery);

  // Update searchTerm when URL changes
  useEffect(() => {
    const query = searchParams.get("query") ?? "";
    setSearchTerm(query);
    setInputValue(query);
  }, [searchParams]);

  // Fetch current user's spaces and space accounts
  const { data: spacesData } = useQuery({
    queryKey: ["spaces", "me"],
    queryFn: () => api.get("/v1/me/spaces", {}),
  });

  const { data: spaceAccountsData } = useQuery({
    queryKey: ["space-accounts", "me"],
    queryFn: () => api.get("/v1/me/space-accounts", {}),
  });

  // Find the primary space account ID
  const primarySpaceAccountId = useMemo(() => {
    if (!spacesData?.data || !spaceAccountsData?.data) return null;

    const primarySpace = spacesData.data.find(space => space.is_primary_space);
    if (!primarySpace) return null;

    const primarySpaceAccount = spaceAccountsData.data.find(
      account => account.space_id === primarySpace.id
    );

    return primarySpaceAccount?.id ?? null;
  }, [spacesData, spaceAccountsData]);

  // React Query to perform the search based on searchTerm
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["sessions-search", searchTerm],
    queryFn: () =>
      api.post("/internal/v1/sessions/search", {
        body: {
          filter_by_space_account_id: searchTerm || null,
          limit: pageSize,
        },
      }),
    enabled: searchTerm.length > 0,
    // 5 minutes
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const handleViewSession = (id: string) => {
    navigate(atlasRoutes.sessionsView({ params: { sessionId: id } }).path);
  };

  const handleSearch = (query: string) => {
    if (query) {
      searchParams.set("query", query);
    } else {
      searchParams.delete("query");
    }
    setSearchParams(searchParams);
  };

  const handleSearchMyAccount = () => {
    if (primarySpaceAccountId) {
      setInputValue(primarySpaceAccountId);
    }
  };

  // Prepare the data for the table
  const tableData = useMemo<SessionSummarySchema[]>(() => data?.data?.results ?? [], [data]);

  return (
    <Container>
      <Flex justify="between" align="center" mb="4">
        <div>
          <h1>Sessions</h1>
          <DescriptionText>Filter by Space Account ID.</DescriptionText>
        </div>
        <Button onClick={() => navigate(atlasRoutes.sessionsCreate({}).path)}>
          Create Session
        </Button>
      </Flex>
      <br />
      {/* Search Form */}
      <SessionsSearchForm
        initialValue={inputValue}
        onSearch={handleSearch}
        onSearchMyAccount={handleSearchMyAccount}
        canSearchMyAccount={!!primarySpaceAccountId}
      />
      {/* Loading and Error States */}
      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching sessions: {error.message}</div>}
      {/* Table or No Results */}
      {tableData.length > 0 ? (
        <SessionsTable data={tableData} onViewSession={handleViewSession} pageSize={pageSize} />
      ) : (
        !isLoading && searchTerm && <div>No sessions found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
`;
